import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Actions } from '@ngrx/effects';
import { COMPONENT_STATE } from '../di/component-state.token';
import { JwtService } from '../services/jwt.service';
import { AbstractCoreComponent } from './abstract-core.component';
import Aura from '@primeng/themes/aura';

@Injectable()
export abstract class AbstractBaseComponent extends AbstractCoreComponent {
  private readonly jwt = inject(JwtService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly location = inject(Location);
  protected readonly actions$ = inject(Actions);

  // Re-inject as required — overrides Core's optional null with the actual service
  protected override readonly state = inject(COMPONENT_STATE);

  colors = {
    emerald: Aura.primitive!.emerald!,
    blue: Aura.primitive!.blue!,
    green: Aura.primitive!.green!,
    red: Aura.primitive!.red!,
    yellow: Aura.primitive!.yellow!,
    orange: Aura.primitive!.orange!,
  };

  elementRef: ElementRef = inject(ElementRef);

  readonly id = signal<string | undefined>(undefined);

  constructor() {
    super();
    this.subscribeToRouteParamsChanges();
  }

  protected subscribeToRouteParamsChanges(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const routeId = params.get('id');
        if (routeId) {
          this.logger.debug(this.name, `Route ID: ${routeId}`);
          this.id.set(routeId);
        }
      });
  }

  get identifier(): number {
    try {
      return Number(this.id());
    } catch {
      return NaN;
    }
  }

  get sub(): number {
    try {
      return Number(this.jwt.sub);
    } catch {
      return NaN;
    }
  }

  set loading(value: boolean) { this.state.loading.set(value); }
  get loading(): boolean { return this.state.isLoading(); }

  set lookup(value: boolean) { this.state.lookup.set(value); }
  get lookup(): boolean { return this.state.isLookup(); }

  set error(value: boolean) { this.state.error.set(value); }
  get error(): boolean { return this.state.hasError(); }

  set idle(value: boolean) { this.state.idle.set(value); }
  get idle(): boolean { return this.state.isIdle(); }
}
